import { ReactRenderer } from '@tiptap/react';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import tippy, { Instance, Props } from 'tippy.js';

import { KeyboardKeys } from '@/shared/utils/navigation';

import { Z_INDEXES } from '@/utils/constants';

import { VariableItem, VariableProps } from '../types/rich-text-editor';
import VariableList from './variable-list';

interface VariableNodeAttrs {
	id: string;
	label?: string;
	token?: string;
	prefix?: string;
	tooltip?: string;
}

interface SuggestionRenderProps extends SuggestionProps<VariableItem, VariableNodeAttrs> {}

/**
 * Replaces the `@…` query with the variable node, then a trailing space so the
 * caret isn't glued to the chip and the user can keep typing immediately.
 */

function insertVariable(
	props: SuggestionRenderProps,
	{ id, label, token, prefix }: VariableNodeAttrs,
	variableProps?: VariableProps
) {
	const inserted = props.editor
		.chain()
		.focus()
		.deleteRange(props.range)
		.insertContent([
			{ type: 'variable', attrs: { id, token, label, prefix } },
			{ type: 'text', text: ' ' },
		])
		.run();

	if (inserted) variableProps?.onSelect?.({ id, label: label ?? '', token: token ?? '', prefix });
}

export default function getVariableSuggestions(variableProps?: VariableProps) {
	return {
		items: ({}: { query: string }) => {
			// Items are handled by VariableList component directly
			return [];
		},

		render: () => {
			let component: ReactRenderer;
			let popup: Instance<Props>[];
			let resizeObserver: ResizeObserver | undefined;

			return {
				onStart: (props: SuggestionRenderProps) => {
					component = new ReactRenderer(VariableList, {
						props: {
							...props,
							variableProps,
							command: (attrs: VariableNodeAttrs) => insertVariable(props, attrs, variableProps),
							onDismiss: () => popup?.[0]?.hide(),
						},
						editor: props.editor,
					});

					if (!props.clientRect) return;

					popup = tippy('body', {
						getReferenceClientRect: props.clientRect as () => DOMRect,
						appendTo: () => document.body,
						content: component.element,
						showOnCreate: true,
						interactive: true,
						trigger: 'manual',
						placement: 'bottom-start',
						zIndex: Z_INDEXES.TIPPY,
					});

					// The dropdown content is async (skeleton → results → empty state), so its
					// height changes after tippy first places the popup. Popper only re-checks
					// flip on an explicit update, so nudge it whenever the content resizes —
					// otherwise a list that grew after mount stays anchored below and runs off
					// the bottom of the screen instead of flipping above the cursor.
					resizeObserver = new ResizeObserver(() => popup[0]?.popperInstance?.update());
					resizeObserver.observe(component.element);
				},

				onUpdate(props: SuggestionRenderProps) {
					component.updateProps({
						...props,
						variableProps,
					command: (attrs: VariableNodeAttrs) => insertVariable(props, attrs, variableProps),
					});

					if (!props.clientRect) {
						return;
					}

					popup[0].setProps({
						getReferenceClientRect: props.clientRect as () => DOMRect,
					});
				},

				onKeyDown(props: SuggestionKeyDownProps) {
					if (props.event?.key === KeyboardKeys.Escape) {
						popup[0].hide();

						return true;
					}

					return (component.ref as { onKeyDown: (props: KeyboardEvent) => boolean })?.onKeyDown(
						props?.event
					);
				},

				onExit() {
					resizeObserver?.disconnect();
					popup[0].destroy();
					component.destroy();
				},
			};
		},
	};
}
